<template>
  <div>
    <nav class="breadcrumbs" aria-label="breadcrumb">
      <div class="container-fluid px-2">
        <div class="main__row">
          <div class="main__col-app">
            <div class="breadcrumbs__scroll-wrapper">
              <div
                ref="scrollListRef"
                class="breadcrumbs__scroll-list"
                :class="{
                  'breadcrumbs__scroll-list--at-start': isAtStart,
                  'breadcrumbs__scroll-list--at-end': isAtEnd,
                }"
                @scroll="onScroll"
              >
                <ol class="breadcrumbs__list">
                  <li class="breadcrumbs__item">
                    <a :href="eshopUrl" class="breadcrumbs__link">{{ $t('breadcrumb.eshop') }}</a>
                  </li>
                  <template v-for="(item, index) in breadcrumbItems" :key="index">
                    <svg class="icon icon-xs mx-1" aria-hidden="true">
                      <use xlink:href="@/assets/images/icons.svg#chevron-right"></use>
                    </svg>
                    <li
                      class="breadcrumbs__item"
                      :class="{ 'breadcrumbs__item--active': item.current }"
                      :aria-current="item.current ? 'page' : undefined"
                    >
                      <RouterLink v-if="item.to" :to="item.to" class="breadcrumbs__link">
                        {{ item.label }}
                      </RouterLink>
                      <span v-else>{{ item.label }}</span>
                    </li>
                  </template>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <div class="container-fluid px-2 pt-2">
      <div class="main__row">
        <div class="main__col-app">
          <a :href="eshopUrl" class="breadcrumbs__back-link">
            <svg class="icon icon-xs me-1" aria-hidden="true">
              <use xlink:href="@/assets/images/icons.svg#chevron-left"></use>
            </svg>
            {{ $t('breadcrumb.backToEshop') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, type RouteLocationRaw } from 'vue-router'

import { useUrls } from '../../composables/url'

interface BreadcrumbItem {
  label: string
  to?: RouteLocationRaw
  current: boolean
}

const { eshopUrl } = useUrls()
const route = useRoute()
const { t } = useI18n()

const scrollListRef = ref<HTMLElement | null>(null)
const isAtStart = ref(true)
const isAtEnd = ref(true)

function onScroll() {
  const el = scrollListRef.value
  if (!el) return
  isAtStart.value = el.scrollLeft <= 0
  isAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = []
  const orderId = route.params.id
  const isOnRoot = !orderId
  const isOnOrder = route.name === 'order'
  const isOnAction = !!orderId && !isOnOrder

  items.push({
    label: t('title'),
    to: isOnRoot ? undefined : '/',
    current: isOnRoot,
  })

  if (orderId) {
    items.push({
      label: `#${orderId}`,
      to: isOnAction ? { name: 'order', params: route.params, query: route.query } : undefined,
      current: isOnOrder,
    })

    if (isOnAction && route.meta.breadcrumb) {
      items.push({
        label: t(route.meta.breadcrumb as string),
        current: true,
      })
    }
  }

  return items
})

watch(
  breadcrumbItems,
  () => {
    const el = scrollListRef.value
    if (!el) return
    el.scrollLeft = el.scrollWidth
    onScroll()
  },
  { immediate: true, flush: 'post' },
)
</script>
